/**
 * Shared authenticated mobile bottom navigation.
 *
 * S012 authority: BottomNav node 38:1104 / rendered instance 38:2797.
 * The component is owned by Layout so feature views never create duplicate
 * navigation bars.
 */
import { Link, useLocation } from 'react-router-dom';
import { RoutePaths } from '@/routes';
import { useAuthStore } from '@/store/authStore';
import './BottomNav.css';

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M10.5 17C14.0899 17 17 14.0899 17 10.5C17 6.91015 14.0899 4 10.5 4C6.91015 4 4 6.91015 4 10.5C4 14.0899 6.91015 17 10.5 17Z" />
      <path d="M18.5 18.5L15 15" strokeLinecap="round" />
    </svg>
  );
}

function FeedIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M18.5 4H5.5C4.11929 4 3 5.11929 3 6.5V17.5C3 18.8807 4.11929 20 5.5 20H18.5C19.8807 20 21 18.8807 21 17.5V6.5C21 5.11929 19.8807 4 18.5 4Z" />
      <path d="M7 9.5H17M7 12.5H14.5M7 15.5H11" strokeLinecap="round" />
    </svg>
  );
}

function ChatIcon({ active }: { readonly active: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M4 5C4 3.895 4.895 3 6 3H18C19.105 3 20 3.895 20 5V14C20 15.105 19.105 16 18 16H9L5.5 20V16C4.672 16 4 15.328 4 14.5V5Z"
        className={active ? 'bottom-nav-icon-active' : undefined}
      />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 12C14.2091 12 16 10.2091 16 8C16 5.79086 14.2091 4 12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12Z" />
      <path d="M4 20C4 16.686 7.582 14 12 14C16.418 14 20 16.686 20 20" strokeLinecap="round" />
    </svg>
  );
}

export function BottomNav() {
  const location = useLocation();
  const registrationKind = useAuthStore((state) => state.registrationKind);
  const profilePath = registrationKind === 'sponsor'
    ? RoutePaths.SPONSOR_PROFILE
    : RoutePaths.ACCOUNT;

  const isCurrent = (path: string, prefix = false) => prefix
    ? location.pathname.startsWith(path)
    : location.pathname === path;
  const chatActive = isCurrent(RoutePaths.CHAT, true);

  return (
    <nav className="bottom-nav" aria-label="Нижняя навигация" data-testid="bottom-nav">
      <Link
        to={RoutePaths.DISCOVER}
        className={`bottom-nav-item${isCurrent(RoutePaths.DISCOVER, true) ? ' bottom-nav-item-active' : ''}`}
        aria-current={isCurrent(RoutePaths.DISCOVER, true) ? 'page' : undefined}
        data-testid="bottom-nav-search"
      >
        <SearchIcon />
        <span>Поиск</span>
      </Link>
      <Link
        to={RoutePaths.FEED}
        className={`bottom-nav-item${isCurrent(RoutePaths.FEED, true) ? ' bottom-nav-item-active' : ''}`}
        aria-current={isCurrent(RoutePaths.FEED, true) ? 'page' : undefined}
        data-testid="bottom-nav-feed"
      >
        <FeedIcon />
        <span>Без фильтров</span>
      </Link>
      <Link
        to={RoutePaths.CHAT}
        className={`bottom-nav-item${chatActive ? ' bottom-nav-item-active' : ''}`}
        aria-current={chatActive ? 'page' : undefined}
        data-testid="bottom-nav-chat"
      >
        <ChatIcon active={chatActive} />
        <span>Чат</span>
      </Link>
      <Link
        to={profilePath}
        className={`bottom-nav-item${isCurrent(profilePath, true) ? ' bottom-nav-item-active' : ''}`}
        aria-current={isCurrent(profilePath, true) ? 'page' : undefined}
        data-testid="bottom-nav-profile"
      >
        <ProfileIcon />
        <span>Профиль</span>
      </Link>
    </nav>
  );
}
